import blogManifest from '../blog-manifest.json'; 
import { format } from 'date-fns';
import MarkdownIt from 'markdown-it';

const md = new MarkdownIt({ html: true });

export interface BlogFrontMatter {
  title: string;
  slug: string;
  date?: number | string;
  tags?: string[];
  excerpt?: string;
  draft?: boolean;
  cover?: string | { image?: string; alt?: string; caption?: string };
  cover_alt?: string;
  cover_caption?: string;
  published_at?: string;
  content?: string;
  language?: string;
}

interface BlogManifestEntry {
  lang: string;
  file: string;
  publicPath: string;
  slug: string;
  frontMatter: BlogFrontMatter;
  hasContent: boolean;
  error?: string;
}

export async function fetchBlogPosts(lang: string = 'en'): Promise<BlogFrontMatter[]> {
  try {
    // Cast the manifest to the correct type
    const manifest = blogManifest as BlogManifestEntry[];
    
    const posts = manifest
      .filter(entry => entry.lang === lang && !entry.error && !entry.frontMatter.draft)
      .map(entry => {
        // Map the blog post data to our expected format
        return {
          ...entry.frontMatter,
          slug: entry.slug || entry.frontMatter.slug || entry.file.replace(/\.md$/, ''),
          // Handle different cover image formats
          cover: typeof entry.frontMatter.cover === 'object' ? entry.frontMatter.cover?.image : entry.frontMatter.cover,
          cover_alt: typeof entry.frontMatter.cover === 'object' ? entry.frontMatter.cover?.alt : entry.frontMatter.cover_alt,
          cover_caption: typeof entry.frontMatter.cover === 'object' ? entry.frontMatter.cover?.caption : entry.frontMatter.cover_caption,
          // Ensure tags is an array
          tags: entry.frontMatter.tags || [],
        };
      })
      .sort((a, b) => {
        // Sort by date in descending order (newest first)
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;
        return dateB - dateA;
      });

    return posts;
  } catch (error) {
    console.error('Error fetching blog posts:', error);
    return [];
  }
}

export async function fetchBlogPost(slug: string, lang: string = 'en'): Promise<{ frontMatter: BlogFrontMatter; content: string } | null> {
  try {
    // Cast the manifest to the correct type
    const manifest = blogManifest as BlogManifestEntry[];
    
    // Find the matching blog post entry using the slug
    const entry = manifest.find(
      e => e.lang === lang && 
      (e.slug === slug || e.frontMatter.slug === slug) && 
      !e.error
    );

    if (!entry) {
      console.warn(`Blog post not found for ${lang}/${slug}`);
      return null;
    }

    // Fetch the actual markdown file
    const response = await fetch(entry.publicPath);
    
    if (!response.ok) {
      throw new Error(`Failed to fetch blog post ${entry.publicPath}`);
    }
    
    const markdown = await response.text();
    
    // Extract the content without frontmatter
    const contentWithoutFrontmatter = markdown.replace(/^---[\s\S]*?---\s*/m, '');
    const renderedContent = md.render(contentWithoutFrontmatter);

    return {
      frontMatter: {
        ...entry.frontMatter,
        slug: entry.slug || entry.frontMatter.slug,
        cover: typeof entry.frontMatter.cover === 'object' ? entry.frontMatter.cover?.image : entry.frontMatter.cover,
        cover_alt: typeof entry.frontMatter.cover === 'object' ? entry.frontMatter.cover?.alt : entry.frontMatter.cover_alt,
        cover_caption: typeof entry.frontMatter.cover === 'object' ? entry.frontMatter.cover?.caption : entry.frontMatter.cover_caption,
        tags: entry.frontMatter.tags || [],
      },
      content: renderedContent
    };
  } catch (error) {
    console.error(`Error fetching blog post ${lang}/${slug}:`, error);
    return null;
  }
}
